import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Form, Badge } from "react-bootstrap";
import Select from "react-select";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import { SvgComponent } from "components";
import { CargaMasiva } from "models/ECF/CargaMasiva";
import useMessage from "hooks/useMessage";

const FILAS_POR_PAGINA = 100;

/**
 * Paso 3: Tabla editable con las filas parseadas del Excel.
 * Permite al usuario revisar, editar, agregar y eliminar filas antes de grabar.
 *
 * Props:
 *   entidad       - value de la entidad (e.g. "Item")
 *   filas         - array inicial / datos externos (errores del backend)
 *   setFilas      - setter del padre (solo se llama al grabar o al recibir datos externos)
 *   onGuardar(filasValidas) - callback para disparar el guardado
 *   disabled      - deshabilita la edición mientras graba
 */
const DataPreviewGrid = ({ entidad, filas, setFilas, onGuardar, disabled }) => {
  const config = CargaMasiva.getConfigEntidad(entidad);

  // ── Estado LOCAL de filas — los edits del grid no tocan el padre ─────────
  const [filasLocales, setFilasLocales] = useState(filas);
  const filasPropRef = useRef(filas);

  // Sincroniza solo cuando el padre cambia `filas` externamente
  // (carga de archivo nuevo o retorno con errores del backend)
  useEffect(() => {
    if (filas !== filasPropRef.current) {
      filasPropRef.current = filas;
      setFilasLocales(filas);
    }
  }, [filas]);

  const [erroresFila, setErroresFila] = useState({});
  const [intentoGrabar, setIntentoGrabar] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const [errorNavPos, setErrorNavPos] = useState(0);
  const [errorNavTarget, setErrorNavTarget] = useState(null);
  const [hoverSigError, setHoverSigError] = useState(false);
  const { Warning } = useMessage();
  const tablaRef = useRef(null);

  // Validación con debounce — no corre en cada tecla, sino 250ms después
  useEffect(() => {
    const timer = setTimeout(() => {
      const nuevosErrores = {};
      filasLocales.forEach((fila, idx) => {
        const err = CargaMasiva.validarFila(entidad, fila);
        if (err) nuevosErrores[idx] = err;
        else if (fila._errorBackend) nuevosErrores[idx] = fila._errorBackend;
      });
      setErroresFila(nuevosErrores);
    }, 250);
    return () => clearTimeout(timer);
  }, [filasLocales, entidad]);

  // Resetear paginación y nav al cambiar entidad
  useEffect(() => {
    setPaginaActual(1);
    setErrorNavPos(0);
    setErrorNavTarget(null);
  }, [entidad]);

  // Scroll al row del error tras cambiar de página
  useEffect(() => {
    if (errorNavTarget === null) return;
    const row = tablaRef.current?.querySelector(`[data-rowidx="${errorNavTarget}"]`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      setErrorNavTarget(null);
    }
  }, [errorNavTarget, paginaActual]);

  if (!config) return null;

  // ── Paginación ──────────────────────────────────────────────────────────────
  const totalPaginas = Math.max(1, Math.ceil(filasLocales.length / FILAS_POR_PAGINA));
  const paginaSegura = Math.min(paginaActual, totalPaginas);
  const idxInicio = (paginaSegura - 1) * FILAS_POR_PAGINA;
  const filasPagina = filasLocales.slice(idxInicio, idxInicio + FILAS_POR_PAGINA);

  const paginaDeIdx = (idx) => Math.floor(idx / FILAS_POR_PAGINA) + 1;

  // ── Índices con error ordenados ─────────────────────────────────────────────
  const indicesConError = Object.keys(erroresFila)
    .map((k) => parseInt(k))
    .sort((a, b) => a - b);

  const totalErrores = indicesConError.length;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleCeldaChange = useCallback((idx, key, value) => {
    setFilasLocales((prev) =>
      prev.map((f, i) =>
        i === idx ? { ...f, [key]: value, _errorBackend: null } : f
      )
    );
  }, []);

  const handleAgregarFila = () => {
    const nuevaFila = { Fila: filasLocales.length + 2 };
    config.campos.forEach((campo) => {
      if (campo.type === "select" && campo.options && campo.options.length > 0) {
        nuevaFila[campo.key] = campo.options[0].value;
      } else {
        nuevaFila[campo.key] = "";
      }
    });
    setFilasLocales((prev) => [...prev, nuevaFila]);
    const nuevaPagina = Math.ceil((filasLocales.length + 1) / FILAS_POR_PAGINA);
    setPaginaActual(nuevaPagina);
  };

  const handleEliminarFila = (idx) => {
    setFilasLocales((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSiguienteError = () => {
    if (indicesConError.length === 0) return;
    const pos = errorNavPos % indicesConError.length;
    const idx = indicesConError[pos];
    setErrorNavPos(pos + 1);
    setErrorNavTarget(idx);
    setPaginaActual(paginaDeIdx(idx));
  };

  const handleGrabarClick = () => {
    if (totalErrores > 0) {
      setIntentoGrabar(true);
      const infoErrores = indicesConError
        .map((idx) => {
          const filaNum = filasLocales[idx]?.Fila || idx + 2;
          const pag = paginaDeIdx(idx);
          return `fila ${filaNum} (pág. ${pag})`;
        })
        .join(", ");
      Warning({
        message: `Hay ${totalErrores} fila${totalErrores > 1 ? "s" : ""} con errores: ${infoErrores}. Corrígelas antes de grabar.`,
        autoDismiss: 4,
      });
      return;
    }
    setIntentoGrabar(false);
    // Sincronizar al padre antes de enviar
    setFilas(filasLocales);
    onGuardar(filasLocales);
  };

  // ── Render celda ────────────────────────────────────────────────────────────
  const renderCelda = (fila, campo, globalIdx) => {
    const valor = fila[campo.key] !== undefined ? fila[campo.key] : "";
    const tieneError =
      campo.required && (valor === "" || valor === null || valor === undefined);

    const estiloInput = {
      borderColor: tieneError ? "#dc3545" : undefined,
      minWidth: "120px",
    };

    if (campo.type === "select" && campo.options) {
      const opciones = campo.options;
      const opcionSeleccionada = opciones.find(
        (o) => String(o.value) === String(valor)
      );
      return (
        <Select
          options={opciones}
          value={opcionSeleccionada || null}
          onChange={(opt) => handleCeldaChange(globalIdx, campo.key, opt ? opt.value : "")}
          isDisabled={disabled}
          menuPortalTarget={document.body}
          styles={{
            container: (base) => ({ ...base, minWidth: 160 }),
            control: (base) => ({
              ...base,
              borderColor: tieneError ? "#dc3545" : base.borderColor,
              minHeight: 32,
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      );
    }

    return (
      <Form.Control
        type={campo.type === "number" ? "number" : "text"}
        value={valor}
        onChange={(e) => handleCeldaChange(globalIdx, campo.key, e.target.value)}
        disabled={disabled}
        style={estiloInput}
        size="sm"
      />
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Barra superior */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Badge bg="secondary">{filasLocales.length} filas</Badge>
          {totalErrores > 0 && (
            <>
              <Badge bg="danger">{totalErrores} con errores</Badge>
              <button
                type="button"
                onClick={handleSiguienteError}
                title="Siguiente error"
                className="animated-button"
              >
                <div
                  className={hoverSigError ? "animated-div-hover" : "animated-div"}
                  style={{
                    height: "38px",
                    width: "38px",
                    backgroundColor: hoverSigError ? "#dc3545" : "transparent",
                    borderColor: "#dc3545",
                  }}
                  onMouseEnter={() => setHoverSigError(true)}
                  onMouseLeave={() => setHoverSigError(false)}
                >
                  <SvgComponent
                    height="22px"
                    width="22px"
                    iconName="next-error"
                    fill={hoverSigError ? "#fff" : "#dc3545"}
                  />
                </div>
              </button>
            </>
          )}
          {totalErrores === 0 && filasLocales.length > 0 && (
            <Badge bg="success">Todas las filas son válidas</Badge>
          )}
        </div>
        <div className="d-flex gap-2 align-items-center">
          <AnimatedIconButton
            onClick={handleAgregarFila}
            iconName="create"
            message="Agregar fila"
            size="38px"
            height="22px"
            width="22px"
            disabled={disabled}
          />
          <AnimatedIconButton
            onClick={handleGrabarClick}
            iconName="save"
            message={`Grabar (${filasLocales.length} filas)`}
            size="38px"
            height="22px"
            width="22px"
            disabled={filasLocales.length === 0 || disabled}
          />
        </div>
      </div>

      {/* Tabla editable */}
      <div ref={tablaRef} style={{ overflowX: "auto", maxHeight: "55vh", overflowY: "auto" }}>
        <table
          className="table table-bordered table-sm"
          style={{ minWidth: "100%", fontSize: "0.85rem" }}
        >
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 2 }}>
            <tr>
              <th style={{ width: 50, position: "sticky", left: 0,  backgroundColor: "#fff", zIndex: 3 }}>#</th>
              <th style={{ width: 46, position: "sticky", left: 50, backgroundColor: "#fff", zIndex: 3 }}></th>
              <th style={{ width: 80, position: "sticky", left: 96, backgroundColor: "#fff", zIndex: 3 }}>Estado</th>
              {config.campos.map((campo) => (
                <th key={campo.key}>
                  {campo.label}
                  {campo.required && (
                    <span className="text-danger ms-1" title="Requerido">*</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filasPagina.map((fila, pageIdx) => {
              const globalIdx = idxInicio + pageIdx;
              const errorFila = erroresFila[globalIdx];
              const stickyBg = errorFila
                ? intentoGrabar ? "#fecaca" : "#fff8f8"
                : "#fff";
              return (
                <tr
                  key={globalIdx}
                  data-rowidx={globalIdx}
                  style={{
                    background: errorFila
                      ? intentoGrabar
                        ? "#fecaca"
                        : "#fff8f8"
                      : undefined,
                    transition: "background 0.3s",
                  }}
                >
                  <td className="text-muted" style={{ verticalAlign: "middle", position: "sticky", left: 0,  backgroundColor: stickyBg, zIndex: 1 }}>
                    {globalIdx + 1}
                  </td>
                  <td style={{ verticalAlign: "middle", position: "sticky", left: 50, backgroundColor: stickyBg, zIndex: 1 }}>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => handleEliminarFila(globalIdx)}
                      disabled={disabled}
                      title="Eliminar fila"
                    >
                      <i className="fas fa-trash" />
                    </Button>
                  </td>
                  <td style={{ verticalAlign: "middle", position: "sticky", left: 96, backgroundColor: stickyBg, zIndex: 1 }}>
                    {errorFila ? (
                      <span
                        className="text-danger"
                        title={errorFila}
                        style={{ fontSize: "0.8rem" }}
                      >
                        <i className="fas fa-exclamation-triangle me-1" />
                        Error
                      </span>
                    ) : (
                      <span className="text-success" style={{ fontSize: "0.8rem" }}>
                        <i className="fas fa-check me-1" />
                        Válido
                      </span>
                    )}
                  </td>
                  {config.campos.map((campo) => (
                    <td key={campo.key} style={{ verticalAlign: "middle", padding: "4px 6px" }}>
                      {renderCelda(fila, campo, globalIdx)}
                    </td>
                  ))}
                </tr>
              );
            })}
            {filasLocales.length === 0 && (
              <tr>
                <td
                  colSpan={config.campos.length + 3}
                  className="text-center text-muted py-4"
                >
                  No hay filas para mostrar. Regresa al paso anterior para cargar un archivo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
          <small className="text-muted">
            Mostrando filas {idxInicio + 1}–{Math.min(idxInicio + FILAS_POR_PAGINA, filasLocales.length)} de {filasLocales.length}
          </small>
          <nav aria-label="Paginación">
            <ul className="pagination mb-0" style={{ maxHeight: 38 }}>
              <li className={`page-item ${paginaSegura === 1 ? "disabled" : ""}`}>
                <a
                  className="page-link gridView-pagination-link"
                  href="#"
                  onClick={(e) => { e.preventDefault(); setPaginaActual((p) => Math.max(1, p - 1)); }}
                >
                  <i className="fas fa-chevron-left" />
                </a>
              </li>
              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pag) => {
                const tieneError = indicesConError.some((idx) => paginaDeIdx(idx) === pag);
                const isActive = paginaSegura === pag;
                return (
                  <li key={pag} className={`page-item ${isActive ? "active" : ""}`}>
                    <a
                      className={`page-link ${isActive ? "gridView-pagination" : "gridView-pagination-link"}`}
                      href="#"
                      onClick={(e) => { e.preventDefault(); setPaginaActual(pag); }}
                      style={tieneError && !isActive ? { color: "#dc3545", borderColor: "#dc3545" } : undefined}
                    >
                      {pag}
                      {tieneError && !isActive && (
                        <i className="fas fa-exclamation-circle ms-1" style={{ fontSize: "0.65rem" }} />
                      )}
                    </a>
                  </li>
                );
              })}
              <li className={`page-item ${paginaSegura === totalPaginas ? "disabled" : ""}`}>
                <a
                  className="page-link gridView-pagination-link"
                  href="#"
                  onClick={(e) => { e.preventDefault(); setPaginaActual((p) => Math.min(totalPaginas, p + 1)); }}
                >
                  <i className="fas fa-chevron-right" />
                </a>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default DataPreviewGrid;
